import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, Lock } from "lucide-react";
import AthleteBadges from "@/components/AthleteBadges";

export default function PublicResults() {
  const [comps, setComps] = useState([]);
  const [compId, setCompId] = useState("");
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/competitions").then((r) => {
      setComps(r.data);
      if (r.data[0]) setCompId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!compId) return;
    api.get("/public/categories", { params: { competition_id: compId } }).then((r) => {
      setCats(r.data);
      if (r.data[0]) setCatId(r.data[0].id);
    });
  }, [compId]);

  useEffect(() => {
    if (!compId || !catId) return;
    api
      .get("/public/ranking", { params: { competition_id: compId, category_id: catId } })
      .then((r) => setData(r.data));
  }, [compId, catId]);

  const podium = data?.ranking?.slice(0, 3) || [];
  const rest = data?.ranking?.slice(3) || [];

  return (
    <div className="min-h-screen relative">
      <img
        src="https://images.unsplash.com/photo-1612441611175-f101b02c930d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxib2R5YnVpbGRpbmclMjBjb21wZXRpdGlvbiUyMHN0YWdlfGVufDB8fHx8MTc4MjEyMzYxOHww&ixlib=rb-4.1.0&q=85"
        alt=""
        className="fixed inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="fixed inset-0 bg-gradient-to-t from-black via-black/85 to-black/60" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 flex items-center justify-center bg-[#C9A84C] text-[#0A0A0A] rounded-sm">
            <Trophy size={26} strokeWidth={2.5} />
          </div>
          <div>
            <div className="overline">Placar Oficial</div>
            <div className="font-display text-2xl font-black tracking-tight leading-none">
              FITS4YOU EVENTS · RESULTADOS
            </div>
          </div>
          <a href="/login" className="ml-auto text-sm text-[var(--text-secondary)] hover:text-white underline">
            Área restrita →
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <select
            data-testid="public-comp"
            className="input-field"
            value={compId}
            onChange={(e) => setCompId(e.target.value)}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome} — {c.data}
              </option>
            ))}
          </select>
          <select
            data-testid="public-cat"
            className="input-field"
            value={catId}
            onChange={(e) => setCatId(e.target.value)}
          >
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </div>

        {data && data.published === false && (
          <div
            className="card p-10 text-center"
            data-testid="public-unpublished"
            style={{ borderColor: "rgba(201,168,76,0.35)" }}
          >
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                 style={{ background: "rgba(201,168,76,0.15)", border: "1px solid #C9A84C" }}>
              <Lock size={26} style={{ color: "#C9A84C" }} />
            </div>
            <div className="overline mb-2">Aguardando Divulgação</div>
            <div className="font-display text-3xl font-black tracking-tight mb-2">
              RESULTADO NÃO DIVULGADO
            </div>
            <div className="text-[var(--text-secondary)] text-sm max-w-md mx-auto">
              O resultado desta categoria será liberado pela organização no momento da entrega do troféu.
              Fique de olho, atualize esta página em instantes!
            </div>
          </div>
        )}

        {data && data.published !== false && podium.length > 0 && podium.some((p) => p.posicao) && (
          <div className="mb-10">
            <div className="overline mb-4">Pódio</div>
            <div className="grid grid-cols-3 gap-4">
              {podium.map((p, i) => {
                const colors = ["#FFCC00", "#C0C0C0", "#CD7F32"];
                const key = `podium-${p.numero ?? "empty"}-${i}`;
                return (
                  <div key={key} className="card p-6 text-center border-2" style={{ borderColor: p.posicao ? colors[i] : "transparent" }}>
                    <div className="font-display text-6xl font-black" style={{ color: colors[i] }}>
                      {p.posicao ? `${p.posicao}º` : "—"}
                    </div>
                    <div className="font-display text-2xl font-black mt-3 tracking-tight">
                      {p.nome || "—"}
                    </div>
                    <div className="text-sm text-[var(--text-secondary)]">{p.equipe}</div>
                    <div className="mt-3 text-sm">
                      <span className="overline">Total</span>
                      <div className="font-display text-3xl font-bold">
                        {p.score_final ?? "—"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data && data.published !== false && (
        <div className="card">
          <table className="table-dense">
            <thead>
              <tr>
                <th className="w-14">Pos.</th>
                <th>Atleta</th>
                <th>Equipe</th>
                <th className="text-right pr-4">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data?.ranking || []).map((r, i) => (
                <tr key={`row-${r.numero ?? "n"}-${r.nome ?? "x"}-${i}`}>
                  <td className="font-display font-bold text-lg tabular-nums">
                    {r.posicao ? `${r.posicao}º` : "—"}
                  </td>
                  <td>
                    <span className="tabular-nums text-[var(--text-secondary)] mr-2">
                      #{r.numero}
                    </span>
                    {r.nome}
                    <AthleteBadges athlete={{ ...r, id: r.athlete_id }} />
                  </td>
                  <td className="text-sm text-[var(--text-secondary)]">{r.equipe || "—"}</td>
                  <td className="text-right pr-4 tabular-nums font-bold">
                    {r.score_final ?? "—"}
                  </td>
                </tr>
              ))}
              {(!data || data.ranking.length === 0) && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-[var(--text-secondary)]">
                    Aguardando resultados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
